/**
 * Home Page Component
 * 
 * Main landing page with search, browse, and management functionality.
 * Features infinite scroll, sticky headers, and modern UI.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import {
  Plus, Search, FolderOpen, LayoutList, LayoutGrid,
  AlertTriangle, Loader2, PenLine, ChevronRight, ChevronDown, CheckCircle2,
  X, Sparkles, Columns3, Activity, Heart
} from 'lucide-react';
import { useDevMode } from '../contexts/DevModeContext';
import api from '../services/api';
import KinkSearch from '../components/KinkSearch';
import KinkCard from '../components/KinkCard';
import EditableKinkRow from '../components/EditableKinkRow';
import AddKinkModal from '../components/AddKinkModal';
import GroupsView from '../components/GroupsView';
import InfoTooltip, { METRIC_TOOLTIPS } from '../components/InfoTooltip';
import { to5Scale, to10Scale, skillToNumber, render5Scale } from '../utils/displayConverters.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { Pulse, Float, Spinner } from '../components/Animations';
import { useCollection } from '../contexts/CollectionContext';
import FilterUnlockBanner from '../components/FilterUnlockBanner';
import useRatingStats from '../hooks/useRatingStats';
import { filterVisibleKinks } from '../utils/kinkVisibility.js';

function HomePage() {
  // Dev mode
  const { isDevMode } = useDevMode();

  // Data Context
  const {
    kinks, loadKinks, isKinksLoaded, kinksLoading, kinksError,
    categories, loadCategories,
    sexActions, loadSexActions, isActionsLoaded, actionsLoading, actionsError
  } = useCollection();

  // Rating stats for unlock banners
  const { totalRatings, basicFilters: filtersUnlocked } = useRatingStats();

  // Location - to detect /actions vs /browse
  const location = useLocation();

  // URL search params
  const [urlSearchParams] = useSearchParams();
  const autoFocusSearch = urlSearchParams.get('focus') === 'search';

  // Data Tab: 'kinks' or 'actions' - route-aware
  const initialTab = location.pathname === '/actions' ? 'actions' : 'kinks';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Local View State
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // View mode: 'card' or 'table'
  const [viewMode, setViewMode] = useState('table');

  // Group by toggle for table view
  const [groupBy, setGroupBy] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Sorting
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);

  // Track which kinks are expanded
  const [expandedKinks, setExpandedKinks] = useState(new Set());
  const [expandedActions, setExpandedActions] = useState(new Set());

  // Sync activeTab with route changes (browse vs actions)
  useEffect(() => {
    const newTab = location.pathname === '/actions' ? 'actions' : 'kinks';
    setActiveTab(newTab);
  }, [location.pathname]);

  // Initial Load (Kinks)
  useEffect(() => {
    if (!isKinksLoaded) {
      loadKinks({}, 1);
    }
    loadCategories();
  }, []);

  // Initial Load (Actions)
  useEffect(() => {
    if (activeTab === 'actions' && !isActionsLoaded) {
      loadSexActions();
    }
  }, [activeTab]);

  // Handle Search
  const handleSearch = useCallback((params) => {
    setSearchParams(params);
    if (activeTab === 'actions') {
      loadSexActions({ search: params.search, sortBy: params.sortBy, sortOrder: params.sortOrder }, true);
    } else {
      loadKinks(params, 1, true).then(data => {
        setTotalItems(data.pagination?.totalItems || 0);
        setHasMore(1 < (data.pagination?.totalPages || 1));
      });
      setCurrentPage(1);
    }
  }, [loadKinks, loadSexActions, activeTab]);

  // Load More (Kinks)
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      try {
        const data = await loadKinks(searchParams, currentPage + 1);
        setCurrentPage(p => p + 1);
        setHasMore(currentPage + 1 < data.pagination.totalPages);
      } finally {
        setLoadingMore(false);
      }
    }
  }, [loadingMore, hasMore, searchParams, currentPage, loadKinks]);

  // Combined loading state for UI - only show loading if no cached data
  const loading = activeTab === 'kinks' ? (kinksLoading && kinks.length === 0) : (actionsLoading && sexActions.length === 0);
  const error = activeTab === 'kinks' ? kinksError : actionsError;

  // Scroll handler for infinite scroll inside the table container
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when scrolled to within 200px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !loadingMore && !loading) {
      loadMore();
    }
  }, [hasMore, loadingMore, loading, loadMore]);

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Reload when sort changes
  useEffect(() => {
    loadKinks(searchParams, 1, true);
  }, [sortBy, sortOrder]);

  // Handle kink update
  const handleKinkUpdate = (updatedKink) => {
    setKinks(prevKinks =>
      prevKinks.map(k => k.id === updatedKink.id ? updatedKink : k)
    );
  };

  // Handle kink delete
  const handleKinkDelete = (kinkId) => {
    setKinks(prevKinks => prevKinks.filter(k => k.id !== kinkId));
    setTotalItems(prev => prev - 1);
  };

  // Handle new kink added
  const handleKinkAdded = (newKink) => {
    setKinks(prevKinks => [newKink, ...prevKinks]);
    loadCategories();
    setTotalItems(prev => prev + 1);
  };

  // Render sort indicator
  const SortIndicator = ({ column }) => {
    if (sortBy !== column) return null;
    return <span className="ml-1 text-wine-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const visibleKinks = filterVisibleKinks(kinks);

  return (
    <>
      <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-charcoal">
        {/* Page Header with Controls */}
        <div className="flex-shrink-0 bg-charcoal-950/80 backdrop-blur-sm border-b border-wine-900/20 px-6 py-3">
          <div className="max-w-[1800px] mx-auto">
            <div className="flex items-center gap-4">
              {/* Search - takes most space */}
              <div className="flex-1">
                <KinkSearch
                  onSearch={handleSearch}
                  categories={categories}
                  loading={loading}
                  compact={true}
                  autoFocus={autoFocusSearch}
                />
              </div>

              {/* View Controls */}
              <div className="flex items-center gap-3">
                {/* Dev Mode: Add Button */}
                {isDevMode && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="btn-primary flex items-center gap-2 text-sm"
                    title="Add a new kink to the database"
                  >
                    <Plus className="w-4 h-4" /> Add New
                  </button>
                )}

                {/* Group By Toggle */}
                {viewMode === 'table' && (
                  <button
                    onClick={() => setGroupBy(!groupBy)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1.5 border ${groupBy
                      ? 'bg-wine-500/20 text-wine-400 border-wine-500/40'
                      : 'bg-charcoal-950 text-gray-400 border-wine-900/20 hover:text-gray-200'
                      }`}
                    title="Organize kinks by their group"
                  >
                    <FolderOpen className="w-4 h-4" />
                    <span className="hidden sm:inline">Groups</span>
                  </button>
                )}

                {/* View Mode Toggle */}
                <div className="flex bg-charcoal-950 rounded-lg p-1 border border-wine-900/20">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-1.5 ${viewMode === 'table'
                      ? 'bg-charcoal-900 text-gray-100'
                      : 'text-gray-400 hover:text-gray-200'
                      }`}
                  >
                    <LayoutList className="w-4 h-4" />
                    <span className="hidden sm:inline">Table</span>
                  </button>
                  <button
                    onClick={() => setViewMode('card')}
                    className={`px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-1.5 ${viewMode === 'card'
                      ? 'bg-charcoal-900 text-gray-100'
                      : 'text-gray-400 hover:text-gray-200'
                      }`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                    <span className="hidden sm:inline">Cards</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Unlock Banner - shown if user hasn't rated 10 items yet */}
        {!filtersUnlocked && totalRatings < 10 && (
          <div className="flex-shrink-0 px-6 pt-3">
            <div className="max-w-[1800px] mx-auto">
              <FilterUnlockBanner
                totalRatings={totalRatings}
                targetFeature="basicFilters"
                compact={true}
              />
            </div>
          </div>
        )}

        {/* Main Content - Data Tables */}
        <div
          className="flex-1 overflow-y-auto w-full"
          onScroll={handleScroll}
          id="main-scroll-container"
        >
          <main className="max-w-[1800px] w-full mx-auto px-6 pt-4 pb-12">
            <AnimatePresence mode="sync">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                {/* Error State */}
                {error && (
                  <div className="glass-card p-6 text-center mb-4">
                    <div className="text-red-400 mb-2 flex items-center justify-center gap-2">
                      <AlertTriangle className="w-5 h-5" /> Error loading kinks
                    </div>
                    <p className="text-dark-400">{error}</p>
                    <button
                      onClick={() => loadKinks(searchParams, 1, true)}
                      className="btn-secondary mt-4"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Empty State */}
                {activeTab === 'kinks' && !loading && !error && visibleKinks.length === 0 && (
                  <div className="glass-card p-12 text-center">
                    <Pulse subtle className="w-24 h-24 mx-auto mb-6 rounded-full bg-dark-800/50 flex items-center justify-center">
                      <Search className="w-12 h-12 text-wine-500/40" />
                    </Pulse>
                    <h3 className="font-display text-xl font-semibold text-dark-200 mb-2">
                      {searchParams.search ? 'No matches found' : 'Your collection is empty'}
                    </h3>
                    <p className="text-dark-400 mb-6 max-w-md mx-auto">
                      {searchParams.search
                        ? `We couldn't find anything matching "${searchParams.search}". Try different keywords or browse all.`
                        : 'Start exploring desire. Add entries manually or browse the archive.'
                      }
                    </p>
                    <div className="flex justify-center gap-3">
                      {searchParams.search && (
                        <button
                          onClick={() => handleSearch({})}
                          className="btn-ghost flex items-center gap-2"
                        >
                          <X className="w-4 h-4" /> Clear Search
                        </button>
                      )}
                      <button onClick={() => setShowAddModal(true)} className="btn-primary flex items-center gap-2">
                        <PenLine className="w-4 h-4" /> Add Manually
                      </button>
                    </div>
                  </div>
                )}

                {/* Hero Section (Only show when not searching and on first page) */}
                {!searchParams.search && currentPage === 1 && activeTab === 'kinks' && (
                  <div className="mb-8 relative overflow-hidden rounded-xl bg-wine-950/30 border border-wine-700/30 p-8 md:p-12">
                    {/* Ambient background glows - burgundy theme */}
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-wine-600/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#C4A35A]/5 rounded-full blur-3xl pointer-events-none" />

                    {/* Celtic border inset */}
                    <div className="absolute inset-4 border border-[#C4A35A]/10 rounded-lg pointer-events-none" />

                    <div className="relative z-10 max-w-3xl">
                      {/* Decorative label with gold accents */}
                      <div className="flex items-center gap-3 mb-5">
                        <div className="h-px w-8 bg-gradient-to-r from-transparent to-[#C4A35A]/60" />
                        <span className="text-[#C4A35A]/80 font-fantasy text-xs uppercase tracking-[0.3em]">The Archive of Desire</span>
                        <div className="h-px w-8 bg-gradient-to-l from-transparent to-[#C4A35A]/60" />
                      </div>

                      {/* Main headline with Cinzel font */}
                      <h1 className="text-4xl md:text-5xl font-fantasy font-semibold text-cream/90 mb-4 leading-tight tracking-wide">
                        Explore the <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C4A35A] via-wine-400 to-[#C4A35A]">Infinite Spectrum</span> of Play.
                      </h1>

                      {/* Gold divider */}
                      <div className="ornate-divider w-40 mb-6" />

                      <p className="text-lg text-cream/50 leading-relaxed max-w-2xl mb-8 font-light">
                        A curated database of kinks, sex acts, and archetypes designed to help you navigate your deepest curiosities with clarity and without shame.
                      </p>

                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-4 px-5 py-3 rounded-xl bg-[#0A0A0A]/60 border border-wine-700/30 backdrop-blur-md">
                          <div className="flex flex-col">
                            <span className="text-2xl font-bold text-[#C4A35A] leading-none">{totalItems}</span>
                            <span className="text-[10px] text-cream/50 uppercase font-bold tracking-wider">Entries</span>
                          </div>
                          <div className="h-8 w-px bg-wine-700/40" />
                          <div className="flex flex-col">
                            <span className="text-2xl font-bold text-[#C4A35A] leading-none">{categories.length}</span>
                            <span className="text-[10px] text-cream/50 uppercase font-bold tracking-wider">Categories</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-cream/50 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-teal-500" />
                          <span>Verified Safe-to-Browse</span>
                        </div>
                      </div>
                    </div>

                    {/* Subtle floating elements for visual interest */}
                    <div className="absolute right-12 top-1/2 -translate-y-1/2 hidden lg:block opacity-15 hover:opacity-25 transition-opacity duration-700 pointer-events-none">
                      <Float slow>
                        <Sparkles className="w-32 h-32 text-[#C4A35A]" />
                      </Float>
                    </div>
                  </div>
                )}

                {/* Table View (Kinks) */}
                {activeTab === 'kinks' && viewMode === 'table' && visibleKinks.length > 0 && (
                  <div className="border border-dark-800 bg-dark-900/50">
                    {/* Sticky Table Header */}
                    <div className="sticky top-0 z-20 bg-dark-950 border-b border-dark-700">
                      <div className={`grid gap-2 py-3 px-4 items-center ${isDevMode
                        ? 'grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px]' /* Dev+Normal: Name, Cat, W, R, Sp, A, Tools */
                        : 'grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px]' /* Normal: Name, Cat, W, R, Sp, A, Actions */
                        }`}>
                        <div
                          onClick={() => handleSort('name')}
                          className="text-dark-300 font-bold text-[10px] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                          title="Kink name and description"
                        >
                          Kink <SortIndicator column="name" />
                        </div>
                        <div
                          onClick={() => handleSort('category')}
                          className="text-dark-300 font-bold text-[10px] uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none truncate"
                          title="Category classification"
                        >
                          Category <SortIndicator column="category" />
                        </div>
                        <div
                          onClick={() => handleSort('weirdnessScore')}
                          className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider cursor-pointer hover:text-wine-300 transition-colors select-none leading-tight"
                          title="Kinkiness: 1-5 scale (1=Vanilla, 5=Extreme)"
                        >
                          <div className="flex flex-col">
                            <span>Kinkiness</span>
                          </div>
                          <SortIndicator column="weirdnessScore" />
                        </div>
                        {/* Removed Realism column - keeping as stat only */}
                        {/* Removed Ethics column - keeping as stat only */}
                        <div
                          onClick={() => handleSort('rarity')}
                          className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider cursor-pointer hover:text-wine-300 transition-colors select-none leading-tight"
                          title="Rarity: 1-5 scale (1=Niche, 5=Common)"
                        >
                          <div className="flex flex-col">
                            <span>Rarity</span>
                          </div>
                          <SortIndicator column="rarity" />
                        </div>
                        <div
                          onClick={() => handleSort('ethics')}
                          className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider cursor-pointer hover:text-wine-300 transition-colors select-none leading-tight"
                          title="Ethics: 1-5 scale (1=Unethical, 5=Ethical)"
                        >
                          <div className="flex flex-col">
                            <span>Ethics</span>
                          </div>
                          <SortIndicator column="ethics" />
                        </div>
                        <div
                          onClick={() => handleSort('socialAcceptability')}
                          className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider cursor-pointer hover:text-wine-300 transition-colors select-none leading-tight"
                          title="Acceptability: 1-5 scale (1=Taboo, 5=Mainstream)"
                        >
                          <div className="flex flex-col">
                            <span>Accept-</span>
                            <span>ability</span>
                          </div>
                          <SortIndicator column="socialAcceptability" />
                        </div>
                        {isDevMode && (
                          <div
                            className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider cursor-default select-none leading-tight"
                            title="Dev Mode Status"
                          >
                            <div className="flex flex-col">
                              <span>Dev</span>
                              <span>Status</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Table Body */}
                    <div className="divide-y divide-dark-800/50">
                      {groupBy ? (
                        // Grouped rendering with section headers
                        (() => {
                          // Organize kinks by group
                          const groupedKinks = {};
                          const ungroupedKinks = [];

                          visibleKinks.forEach(kink => {
                            if (kink.group) {
                              const groupId = kink.group.id;
                              if (!groupedKinks[groupId]) {
                                groupedKinks[groupId] = {
                                  group: kink.group,
                                  kinks: []
                                };
                              }
                              groupedKinks[groupId].kinks.push(kink);
                            } else {
                              ungroupedKinks.push(kink);
                            }
                          });

                          const toggleGroupCollapse = (groupId) => {
                            setCollapsedGroups(prev => {
                              const next = new Set(prev);
                              if (next.has(groupId)) next.delete(groupId);
                              else next.add(groupId);
                              return next;
                            });
                          };

                          return (
                            <>
                              {/* Grouped kinks with section headers */}
                              {Object.values(groupedKinks).map(({ group, kinks: groupKinks }) => (
                                <div key={group.id}>
                                  {/* Group Header */}
                                  <button
                                    onClick={() => toggleGroupCollapse(group.id)}
                                    className="w-full flex items-center gap-4 px-5 py-3 bg-gradient-to-r from-dark-800/90 to-dark-800/60 hover:from-dark-800 hover:to-dark-800/80 transition-all sticky top-0 z-10 border-b border-dark-700/50 backdrop-blur-sm"
                                  >
                                    <ChevronRight className={`w-5 h-5 text-wine-500 transition-transform duration-200 ${collapsedGroups.has(group.id) ? '' : 'rotate-90'}`} />
                                    <span className="text-2xl">{group.icon}</span>
                                    <span className="font-bold text-dark-50 text-lg tracking-tight">{group.name}</span>
                                    <span className="text-sm text-wine-400/70 ml-1 font-medium">({groupKinks.length})</span>
                                  </button>

                                  {/* Group Kinks */}
                                  {!collapsedGroups.has(group.id) && groupKinks.map((kink, idx) => (
                                    <EditableKinkRow
                                      key={kink.id}
                                      kink={kink}
                                      categories={categories}
                                      onUpdate={handleKinkUpdate}
                                      onDelete={handleKinkDelete}
                                      rowIndex={idx}
                                      showExtendedColumns={showExtendedColumns}
                                      isDevMode={isDevMode}
                                    />
                                  ))}
                                </div>
                              ))}

                              {/* Ungrouped kinks section */}
                              {ungroupedKinks.length > 0 && (
                                <div>
                                  <div className="px-4 py-2 bg-dark-800/50 border-b border-dark-700">
                                    <span className="text-sm text-dark-500">Ungrouped ({ungroupedKinks.length})</span>
                                  </div>
                                  {ungroupedKinks.map((kink, idx) => (
                                    <EditableKinkRow
                                      key={kink.id}
                                      kink={kink}
                                      categories={categories}
                                      onUpdate={handleKinkUpdate}
                                      onDelete={handleKinkDelete}
                                      rowIndex={idx}
                                      showExtendedColumns={showExtendedColumns}
                                      isDevMode={isDevMode}
                                    />
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        // Normal flat rendering
                        visibleKinks.map((kink, idx) => (
                          <EditableKinkRow
                            key={kink.id}
                            kink={kink}
                            categories={categories}
                            onUpdate={handleKinkUpdate}
                            onDelete={handleKinkDelete}
                            rowIndex={idx}
                            isDevMode={isDevMode}
                          />
                        ))
                      )}
                      {/* Loading indicator at bottom */}
                      {loadingMore && (
                        <div className="py-4 text-center text-dark-400 flex items-center justify-center gap-2">
                          <Spinner size="sm" />
                          Loading more...
                        </div>
                      )}
                      {!hasMore && kinks.length > 0 && (
                        <div className="py-4 text-center text-dark-500 text-sm">
                          All {totalItems} kinks loaded
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Card View (Kinks) */}
                {activeTab === 'kinks' && viewMode === 'card' && visibleKinks.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {visibleKinks.map((kink) => (
                      <KinkCard key={kink.id} kink={kink} />
                    ))}
                  </div>
                )}

                {/* Sex Actions Content */}
                {activeTab === 'actions' && (
                  <>
                    {/* Hero Section for Sex Actions */}
                    {!searchParams.search && currentPage === 1 && (
                      <div className="mb-8 relative overflow-hidden rounded-2xl bg-dark-900/40 border border-dark-800 p-8 md:p-12">
                        {/* Ambient background glows */}
                        <div className="absolute -top-24 -right-24 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

                        <div className="relative z-10 max-w-3xl">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="h-px w-8 bg-amber-500" />
                            <span className="text-amber-400 font-bold text-xs uppercase tracking-[0.2em]">The Art of Pleasure</span>
                          </div>
                          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 leading-tight">
                            Master the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-rose-500">Techniques</span> of Intimacy
                          </h1>
                          <p className="text-lg text-dark-300 leading-relaxed max-w-2xl mb-8 font-light italic">
                            A comprehensive guide to sexual positions, techniques, and practices — from foundational to adventurous.
                          </p>

                          <div className="flex flex-wrap gap-4">
                            <div className="flex items-center gap-4 px-5 py-3 rounded-xl bg-dark-950/50 border border-dark-800/50 backdrop-blur-md">
                              <div className="flex flex-col">
                                <span className="text-2xl font-bold text-white leading-none">{sexActions.length}</span>
                                <span className="text-[10px] text-dark-500 uppercase font-bold tracking-wider">Techniques</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 text-dark-400 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              <span>Risk-Rated & Skill-Graded</span>
                            </div>
                          </div>
                        </div>

                        <div className="absolute right-12 top-1/2 -translate-y-1/2 hidden lg:block opacity-20 hover:opacity-40 transition-opacity duration-700 pointer-events-none">
                          <Float slow>
                            <Activity className="w-32 h-32 text-amber-500" />
                          </Float>
                        </div>
                      </div>
                    )}

                    {/* Sex Actions Table */}
                    <div className="h-full border border-dark-800 bg-dark-900/50 overflow-y-auto">
                      {/* Actions Table Header - Aligned with Kinks Table */}
                      <div className="sticky top-0 z-20 bg-dark-950 border-b border-dark-700">
                        <div className="grid grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px] gap-2 py-3 px-4 items-center">
                          <div className="text-dark-300 font-bold text-[10px] uppercase tracking-wider">Action</div>
                          <div className="text-dark-300 font-bold text-[10px] uppercase tracking-wider">Category</div>
                          <div className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider leading-tight" title="Intensity: 1-5 scale (1=Mild, 5=High)">
                            <div className="flex flex-col">
                              <span>Intensity</span>
                            </div>
                          </div>
                          <div className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider leading-tight" title="Risk: 1-5 scale (1=Safe, 5=Dangerous)">
                            <div className="flex flex-col">
                              <span>Risk</span>
                            </div>
                          </div>
                          <div className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider leading-tight" title="Skill: 1-5 scale (1=Novice, 5=Export)">
                            <div className="flex flex-col">
                              <span>Skill</span>
                            </div>
                          </div>
                          <div className="text-center text-wine-400 font-bold text-[10px] uppercase tracking-wider leading-tight" title="Acceptability: 1-5 scale (1=Taboo, 5=Mainstream)">
                            <div className="flex flex-col">
                              <span>Accept-</span>
                              <span>ability</span>
                            </div>
                          </div>
                          {isDevMode && <div className="text-center text-dark-400 font-bold text-xs uppercase tracking-wide">✎</div>}
                        </div>
                      </div>

                      {/* Actions Table Body */}
                      <div className="divide-y divide-dark-800/50">
                        {actionsLoading && (
                          <div className="py-8 text-center text-dark-400">
                            <Spinner size="md" className="mx-auto mb-2" />
                            Loading sex actions...
                          </div>
                        )}

                        {actionsError && (
                          <div className="py-8 text-center text-red-400">
                            <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
                            {actionsError}
                          </div>
                        )}

                        {!actionsLoading && !actionsError && sexActions.length === 0 && (
                          <div className="py-8 text-center text-dark-500">
                            No sex actions found. Run migration scripts to populate.
                          </div>
                        )}

                        {!actionsLoading && sexActions.map((action) => (
                          <React.Fragment key={action.id}>
                            {/* Main Row - Click to navigate, but linked kinks button expands */}
                            <div className="grid grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px] gap-2 py-3 px-4 hover:bg-dark-800/30 transition-colors">
                              {/* Name + Description + Linked Kinks Toggle - Indented to match Kinks Row */}
                              <div className="flex items-start gap-2 min-w-0">
                                {/* Spacer to match Kinks 'Related' button width */}
                                <div className="w-7 flex-shrink-0" />

                                <div className="min-w-0 flex-1">
                                  <Link to={`/action/${action.id}`} className="block">
                                    <div className="font-bold text-dark-100 text-sm">{action.name}</div>
                                    {action.aliases && action.aliases.length > 0 && (
                                      <div className="text-dark-500 text-xs italic truncate">
                                        aka {action.aliases.join(', ')}
                                      </div>
                                    )}
                                    <div className="text-dark-500 text-xs line-clamp-1 mt-0.5">{action.description}</div>
                                  </Link>
                                  {/* Linked Kinks Toggle (if any) */}
                                  {action.kinks && action.kinks.length > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpandedActions(prev => {
                                          const next = new Set(prev);
                                          if (next.has(action.id)) {
                                            next.delete(action.id);
                                          } else {
                                            next.add(action.id);
                                          }
                                          return next;
                                        });
                                      }}
                                      className="text-[10px] text-wine-500/70 mt-1 hover:text-wine-400 flex items-center gap-1 font-bold uppercase tracking-tighter"
                                    >
                                      {expandedActions.has(action.id) ? (
                                        <ChevronDown className="w-3 h-3" />
                                      ) : (
                                        <ChevronRight className="w-3 h-3" />
                                      )}
                                      🔗 {action.kinks.length} linked kink{action.kinks.length > 1 ? 's' : ''}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Category - Plain text to match kinks */}
                              <Link to={`/action/${action.id}`} className="text-dark-400 text-sm truncate flex items-center">
                                {action.category || '-'}
                              </Link>

                              {/* Intensity - Display as 1-5 dots */}
                              <Link to={`/action/${action.id}`} className="flex items-center justify-center">
                                <div className="px-2 py-1 rounded-md bg-dark-800/40">
                                  {render5Scale(to5Scale(action.intensity * 10))}
                                </div>
                              </Link>

                              {/* Risk Level - Display as 1-5 dots */}
                              <Link to={`/action/${action.id}`} className="flex items-center justify-center">
                                <div className="px-2 py-1 rounded-md bg-dark-800/40">
                                  {render5Scale(to5Scale((action.risk || 0) * 10))}
                                </div>
                              </Link>

                              {/* Skill Level - Display as 1-5 dots */}
                              <Link to={`/action/${action.id}`} className="flex items-center justify-center">
                                <div className="px-2 py-1 rounded-md bg-dark-800/40">
                                  {render5Scale(skillToNumber(action.skill))}
                                </div>
                              </Link>

                              {/* Acceptability - Display as 1-5 dots */}
                              <Link to={`/action/${action.id}`} className="flex items-center justify-center">
                                <div className="px-2 py-1 rounded-md bg-dark-800/40">
                                  {render5Scale(to5Scale(action.socialAcceptability || 50))}
                                </div>
                              </Link>

                              {/* Edit Button */}
                              {isDevMode && (
                                <div className="flex items-center justify-center">
                                  <button className="text-dark-500 hover:text-primary-400 transition-colors">
                                    <PenLine className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Expanded Linked Kinks Panel */}
                            {expandedActions.has(action.id) && action.kinks && action.kinks.length > 0 && (
                              <div className="bg-dark-900/80 border-l-2 border-wine-500/30 ml-4 mb-2 p-3 rounded-lg">
                                <div className="text-xs text-dark-400 mb-3 font-medium">Linked Kinks:</div>
                                <div className="grid gap-2">
                                  {action.kinks.map(kink => (
                                    <Link
                                      key={kink.id}
                                      to={`/kink/${kink.id}`}
                                      className="flex items-start gap-3 p-2.5 bg-dark-800/50 hover:bg-dark-700/50 rounded-lg transition-colors border border-dark-700/50 hover:border-wine-500/30 group"
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-dark-100 group-hover:text-wine-400 transition-colors">{kink.name}</span>
                                          {kink.category && (
                                            <span className="text-[10px] px-1.5 py-0.5 bg-dark-700 text-dark-400 rounded">{kink.category}</span>
                                          )}
                                        </div>
                                        {kink.summary && (
                                          <p className="text-xs text-dark-500 mt-0.5 line-clamp-2">{kink.summary}</p>
                                        )}
                                      </div>
                                      <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400 shrink-0 mt-0.5" />
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      {!actionsLoading && sexActions.length > 0 && (
                        <div className="py-4 text-center text-dark-500 text-sm border-t border-dark-800">
                          {sexActions.length} sex actions loaded
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Loading States (Kinks only) */}
                {activeTab === 'kinks' && loading && kinks.length === 0 && (
                  <div className="space-y-2 mt-4">
                    {[...Array(8)].map((_, i) => (
                      <Pulse key={i} className="glass-card p-4">
                        <div className="h-4 bg-dark-700 rounded w-1/3 mb-2" />
                        <div className="h-3 bg-dark-800 rounded w-1/4" />
                      </Pulse>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </main>

          {/* Modals */}
          <AddKinkModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            categories={categories}
            onKinkAdded={handleKinkAdded}
          />
        </div>
      </div>
    </>
  );
}

export default HomePage;
